import { authenticatedProApiFetch } from "./pro-api.client";

export type CommandTransport = "mqtt" | "shadow";
export type CommandDangerLevel = "normal" | "disruptive" | "irreversible";
export type CommandIotDomain = "sanctuary" | "powersave";
export type CommandComponentScope = "ems" | "pcs" | "dcdc" | "hvac" | "pdu" | "pmu";
export type CommandResponseRender = "status" | "kv" | "registers" | "raw";
export type CommandSuccessPredicate = "st_if_present" | "any" | "registers_succ";
export type CommandExecutionStatus = "sent" | "succeeded" | "failed" | "timed_out" | "rejected";
export type CommandExecutionSource = "pro-web" | "mobile" | "api" | "smart-web-observed";
export type CommandParamType =
  | "string"
  | "integer"
  | "number"
  | "boolean"
  | "enum"
  | "register"
  | "register_list"
  | "register_values";

export interface CommandPreconditions {
  wcmMinFw?: string;
  wcmMaxFw?: string;
  communicatorTypes?: Array<"wcm" | "ems-c">;
  maxInverters?: number;
  requiresComponentType?: string;
}

export interface CommandParamField {
  key: string;
  label: string;
  type: CommandParamType;
  required?: boolean;
  help?: string;
  unit?: string;
  min?: number;
  max?: number;
  default?: unknown;
  options?: Array<{ value: string; label: string }>;
  asString?: boolean;
  requires?: CommandPreconditions;
}

export interface CommandDefinition {
  id: string;
  key: string;
  iotDomain: CommandIotDomain;
  componentScope: CommandComponentScope | null;
  category: string;
  displayName: string;
  description: string | null;
  transport: CommandTransport;
  payloadTemplate: Record<string, unknown>;
  params: CommandParamField[];
  responseRender: CommandResponseRender;
  successPredicate: CommandSuccessPredicate;
  timeoutMs: number;
  dangerLevel: CommandDangerLevel;
  confirmationText: string | null;
  preconditions: CommandPreconditions;
  preRead: { mode: "registers" | "batch"; param: string } | null;
  sortOrder: number;
  enabled: boolean;
  version: number;
  updatedAt: string;
}

export type CommandDefinitionInput = Omit<CommandDefinition, "id" | "version" | "updatedAt">;

export interface ProductCommand {
  id: string;
  key: string;
  componentScope: CommandComponentScope | null;
  category: string;
  displayName: string;
  description: string | null;
  transport: CommandTransport;
  params: CommandParamField[];
  responseRender: CommandResponseRender;
  timeoutMs: number;
  dangerLevel: CommandDangerLevel;
  confirmationText: string | null;
  hasPreRead: boolean;
  sortOrder: number;
  available: boolean;
  unavailableReason: string | null;
  unverified: string[];
}

export interface CommandAccess {
  canRead: boolean;
  canSend: boolean;
  canSendDisruptive: boolean;
}

export interface ProductCommandsResponse {
  data: ProductCommand[];
  access: CommandAccess;
  device: {
    iotDomain: CommandIotDomain;
    isSimulated: boolean;
    connected: boolean;
    communicator: { type: "wcm" | "ems-c" | null; fw: string | null };
    inverterCount: number | null;
  };
}

export interface CommandExecution {
  id: string;
  productId: string;
  productName?: string;
  definitionId: string | null;
  commandKey: string;
  displayName: string | null;
  transport: CommandTransport;
  topic: string | null;
  source: CommandExecutionSource;
  actorUserId: string | null;
  actorName: string | null;
  params: Record<string, unknown> | null;
  requestPayload: unknown;
  preReadPayload: unknown;
  preReadResult: unknown;
  responsePayload: unknown;
  status: CommandExecutionStatus;
  phase: "pre_read" | "main";
  error: string | null;
  sentAt: string | null;
  timeoutAt: string | null;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  responseRender: CommandResponseRender;
  dangerLevel: CommandDangerLevel | null;
  category: string | null;
}

export interface CommandExecutionPage {
  data: CommandExecution[];
  nextCursor: string | null;
}

export interface CommandExecutionQuery {
  productId?: string;
  status?: string[];
  source?: string[];
  commandKey?: string[];
  from?: string;
  to?: string;
  q?: string;
  cursor?: string | null;
  limit?: number;
}

export function commandChannel(productId: string): string {
  return `commands:products:${productId}`;
}

// ─── Catalog ──────────────────────────────────────────────────────────────────

export async function fetchCommandDefinitions(token: string): Promise<CommandDefinition[]> {
  const res = await authenticatedProApiFetch<{ data: CommandDefinition[] }>("/command-definitions", token);
  return res.data;
}

export async function fetchCommandDefinition(token: string, id: string): Promise<CommandDefinition> {
  return authenticatedProApiFetch<CommandDefinition>(`/command-definitions/${id}`, token);
}

export async function createCommandDefinition(
  token: string,
  body: CommandDefinitionInput,
): Promise<CommandDefinition> {
  return authenticatedProApiFetch<CommandDefinition>("/command-definitions", token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateCommandDefinition(
  token: string,
  id: string,
  body: Partial<CommandDefinitionInput>,
): Promise<CommandDefinition> {
  return authenticatedProApiFetch<CommandDefinition>(`/command-definitions/${id}`, token, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export async function deleteCommandDefinition(token: string, id: string): Promise<void> {
  await authenticatedProApiFetch(`/command-definitions/${id}`, token, { method: "DELETE" });
}

export async function previewCommandPayload(
  token: string,
  definition: Partial<CommandDefinitionInput>,
  params: Record<string, unknown>,
): Promise<{ payload: unknown }> {
  return authenticatedProApiFetch<{ payload: unknown }>("/command-definitions/preview", token, {
    method: "POST",
    body: JSON.stringify({ definition, params }),
  });
}

// ─── Product commands ─────────────────────────────────────────────────────────

export async function fetchProductCommands(token: string, productId: string): Promise<ProductCommandsResponse> {
  return authenticatedProApiFetch<ProductCommandsResponse>(`/products/${productId}/commands`, token);
}

export async function sendProductCommand(
  token: string,
  productId: string,
  body: { definitionId: string; params: Record<string, unknown> },
): Promise<CommandExecution> {
  return authenticatedProApiFetch<CommandExecution>(`/products/${productId}/commands`, token, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ─── History ──────────────────────────────────────────────────────────────────

export async function fetchCommandExecutions(
  token: string,
  query: CommandExecutionQuery,
): Promise<CommandExecutionPage> {
  const qs = new URLSearchParams();
  if (query.productId) qs.set("productId", query.productId);
  if (query.status?.length) qs.set("status", query.status.join(","));
  if (query.source?.length) qs.set("source", query.source.join(","));
  if (query.commandKey?.length) qs.set("commandKey", query.commandKey.join(","));
  if (query.from) qs.set("from", query.from);
  if (query.to) qs.set("to", query.to);
  if (query.q) qs.set("q", query.q);
  if (query.cursor) qs.set("cursor", query.cursor);
  qs.set("limit", String(query.limit ?? 50));
  return authenticatedProApiFetch<CommandExecutionPage>(`/command-executions?${qs.toString()}`, token);
}

export async function fetchCommandExecution(token: string, id: string): Promise<CommandExecution> {
  return authenticatedProApiFetch<CommandExecution>(`/command-executions/${id}`, token);
}
